<template>
    <section ref="generatorSection" class="image-generator-section">
        <div class="container">
            <div class="generator-container">
                <!-- Generator Header -->
                <div class="generator-header">
                    <div class="generator-title">
                        <h1>{{ title }}</h1>
                        <p class="generator-description" v-html="description"></p>
                        <div class="rainbow-bar"></div>
                    </div>
                </div>

                <!-- Generator Interface -->
                <ImageGenerator
                    :model-name="modelName"
                    :model-key="modelKey"
                    :default-tab="defaultTab"
                    @generate="handleGenerate"
                >
                    <template #model-logo>
                        <slot name="logo">
                            <div class="default-logo">{{ modelName.charAt(0) }}</div>
                        </slot>
                    </template>
                </ImageGenerator>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import ImageGenerator from '~/components/ImageGenerator.vue'

interface Props {
    title: string
    description: string
    modelName: string
    modelKey: string
    defaultTab?: number
    redirectUrl?: string
}

const props = withDefaults(defineProps<Props>(), {
    redirectUrl: '/ai-image',
    defaultTab: 0,
})

const emit = defineEmits<{
    generate: [data: { prompt: string; image?: File; tabType: 'text-to-image' | 'image-to-image' }]
}>()

const generatorSection = ref<HTMLElement | null>(null)

const handleGenerate = (data: {
    prompt: string
    image?: File
    tabType: 'text-to-image' | 'image-to-image'
}) => {
    emit('generate', data)
}

// 滚动到生成器区域
const scrollToSection = () => {
    if (generatorSection.value) {
        generatorSection.value.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }
}

// 暴露方法给父组件
defineExpose({
    scrollToSection,
})
</script>

<style lang="less" scoped>
.image-generator-section {
    padding: 0;
    background: transparent;
    min-height: calc(100vh - 120px);
    position: relative;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
}

.generator-container {
    max-width: 900px;
    margin: 0 auto;
    padding: 0 1.5rem;
    position: relative;
    z-index: 1;
}

.generator-header {
    text-align: center;
    padding: 1rem 0 1rem;
    margin-bottom: 0;
}

.generator-title {
    text-align: center;
    margin-bottom: 0;

    h1 {
        font-size: 3.75rem;
        font-weight: 700;
        color: white;
        margin-bottom: 0.75rem;
        line-height: 1.2;
    }
}

.generator-description {
    font-size: 1rem;
    color: #adadad;
    max-width: 700px;
    margin: 0 auto 0.75rem;
    line-height: 1.5;
    text-align: center;

    :deep(.highlight-red) {
        color: #ef4444;
        font-weight: 600;
    }
}

.rainbow-bar {
    width: 60px;
    height: 3px;
    background: linear-gradient(
        90deg,
        #ef4444,
        #f97316,
        #eab308,
        #22c55e,
        #3b82f6,
        #8b5cf6
    );
    border-radius: 2px;
    margin: 0 auto 1rem;
    position: relative;

    &::before {
        content: '';
        position: absolute;
        left: -8px;
        top: 50%;
        transform: translateY(-50%);
        width: 6px;
        height: 6px;
        background: white;
        border-radius: 50%;
    }
}

.default-logo {
    width: 1.25rem;
    height: 1.25rem;
    background: linear-gradient(135deg, #ff6b6b, #ee5a24);
    border-radius: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 700;
    color: white;
}

@media (max-width: 768px) {
    .generator-title h1 {
        font-size: 2rem;
    }

    .generator-description {
        font-size: 0.875rem;
        padding: 0 1rem;
    }
}

@media (max-width: 480px) {
    .generator-title h1 {
        font-size: 1.5rem;
    }

    .generator-description {
        font-size: 0.8rem;
        padding: 0 0.75rem;
    }
}
</style>

